package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import kotlin.coroutines.resume
import kotlin.math.roundToInt
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

/**
 * A measure over time, with a crosshair and a restrained first-mount reveal.
 *
 * The full geometry is present as a quiet preview on the first frame, so a paused background tab
 * or a stalled animation never withholds the data. A clipped, full-strength copy settles over it
 * without scaling or distorting the strokes. The table and pointer targets are never clipped.
 */
@Composable
fun LLineChart(
    series: List<ChartSeries>,
    modifier: Modifier = Modifier,
    height: Int = 220,
    area: Boolean = false,
    valueFormat: (Double) -> String = { formatCount(it.roundToInt()) },
    emptyMessage: String = t("ds.ds.lline_chart.nothing_to_plot_yet", "Nothing to plot yet."),
    /** Names what the accessible table beneath the plot counts. */
    caption: String = series.joinToString(", ") { it.name },
) {
    val colorMode = ColorMode.current
    val palette = colorMode.toSitePalette()
    val colors = ChartPalette.categorical(colorMode)
    val steps = series.firstOrNull()?.points?.size ?: 0
    if (series.isEmpty() || steps == 0) {
        ChartEmpty(emptyMessage)
        return
    }
    if (steps == 1) {
        SingleChartPoint(series, valueFormat)
        return
    }

    val hoveredState = remember { mutableStateOf<Int?>(null) }
    val hovered = hoveredState.value
    val reducedMotion = rememberReducedMotion()
    val revealComplete = rememberLineChartRevealComplete(reducedMotion)
    // **The preview is scaffolding, and scaffolding comes down.**
    //
    // The grey geometry exists so the chart is readable before the sweep opens; once the coloured
    // marks are fully drawn it sits underneath them for ever, doubling every path, point and label
    // in the DOM for the life of the page. `revealComplete` *starts* the 180ms clip-path
    // transition rather than ending it, so the preview cannot come down at the flip — it has to
    // outlast the sweep it is covering.
    //
    // Safe even if the transition never runs: the clip-path's target is the resting state, so a
    // suspended tab shows the coloured marks in full and the grey underneath them is redundant
    // either way.
    var previewMounted by remember { mutableStateOf(true) }
    if (revealComplete && previewMounted) {
        LaunchedEffect(Unit) {
            delay(Motion.StandardMs.toLong())
            previewMounted = false
        }
    }
    val width = 720.0
    val padLeft = 44.0
    val padRight = 12.0
    val padTop = 12.0
    val padBottom = 26.0
    val plotWidth = width - padLeft - padRight
    val plotHeight = height - padTop - padBottom
    val axis = lineChartAxis(series.flatMap { it.points }.map { it.value })
    fun x(index: Int) = padLeft + plotWidth * index / (steps - 1)
    fun y(value: Double) = axis.y(value, padTop, plotHeight)
    fun explore(clientX: Double, target: Element?) {
        val box = target?.getBoundingClientRect() ?: return
        if (box.width <= 0) return
        hoveredState.value = lineChartIndexForClientX(
            clientX = clientX,
            elementLeft = box.left,
            elementWidth = box.width,
            steps = steps,
            logicalWidth = width,
            plotLeft = padLeft,
            plotWidth = plotWidth,
        )
    }
    fun focusExplorer(target: Element?) {
        (target?.parentElement as? HTMLElement)?.focus()
    }

    Column(modifier.fillMaxWidth().gap(Space.SM)) {
        if (series.size > 1) ChartLegend(series.map { it.name }, colors)
        Div(
            Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("overflow-x", "auto")
                    property("overscroll-behavior-inline", "contain")
                }
                .toAttrs {
                    attr("data-chart-explorer", "")
                    attr("tabindex", "0")
                    attr("role", "group")
                    attr(
                        "aria-label",
                        trf(
                            "ds.ds.lline_chart.caption_use_left_and_right_arrow",
                            "{caption}. Use Left and Right arrow keys to inspect each step.",
                            "caption" to caption,
                        ),
                    )
                    onFocus { if (hoveredState.value == null) hoveredState.value = steps - 1 }
                    onBlur { hoveredState.value = null }
                    onKeyDown { event ->
                        val next = when (event.key) {
                            "ArrowLeft" -> ((hoveredState.value ?: steps) - 1).coerceAtLeast(0)
                            "ArrowRight" -> ((hoveredState.value ?: -1) + 1).coerceAtMost(steps - 1)
                            "Home" -> 0
                            "End" -> steps - 1
                            else -> null
                        }
                        if (next != null) {
                            event.preventDefault()
                            hoveredState.value = next
                        }
                    }
                },
        ) {
            Svg({
                attr("viewBox", "0 0 ${width.round()} $height")
                attr("preserveAspectRatio", "xMinYMin meet")
                attr("width", width.round())
                attr("height", height.toString())
                // **The drawing scales; it does not scroll off the card.** `min-width: 720px` inside
                // an `overflow-x: auto` box meant that in a 358px card a reader saw the left half
                // only — a flat line running to the edge, one axis label, no marker and no cue that
                // there was more — on a card whose own tile said "80 in the last 30 days".
                //
                // `max-width` is what keeps the element's box exactly equal to the drawing at every
                // size: below 720 the SVG shrinks and `height: auto` follows the viewBox's ratio, at
                // or above it the box stops at the logical width instead of letterboxing. That
                // identity is load-bearing — `explore()` maps a pointer's X through
                // `getBoundingClientRect().width` against the logical 720, and any blank band inside
                // the box would offset every hover by the width of it.
                attr(
                    "style",
                    "display:block; width:100%; max-width:${width.round()}px; height:auto; " +
                        "touch-action:pan-y;",
                )
                // The real table below owns accessibility. The drawing owns pointer exploration.
                attr("aria-hidden", "true")
                attr("focusable", "false")
                onMouseMove { event ->
                    explore(event.clientX.toDouble(), event.currentTarget as? Element)
                }
                onMouseLeave { hoveredState.value = null }
                onClick { event ->
                    focusExplorer(event.currentTarget as? Element)
                    explore(event.clientX.toDouble(), event.currentTarget as? Element)
                }
                addEventListener("pointerdown") { event ->
                    val clientX = (event.nativeEvent.asDynamic().clientX as? Number)?.toDouble()
                    focusExplorer(event.currentTarget as? Element)
                    clientX?.let { explore(it, event.currentTarget as? Element) }
                }
                addEventListener("pointermove") { event ->
                    val clientX = (event.nativeEvent.asDynamic().clientX as? Number)?.toDouble()
                    clientX?.let { explore(it, event.currentTarget as? Element) }
                }
                onTouchStart { event ->
                    val touch = event.touches.item(0) ?: event.changedTouches.item(0)
                    focusExplorer(event.currentTarget as? Element)
                    touch?.let { explore(it.clientX.toDouble(), event.currentTarget as? Element) }
                }
                onTouchMove { event ->
                    val touch = event.touches.item(0) ?: event.changedTouches.item(0)
                    touch?.let { explore(it.clientX.toDouble(), event.currentTarget as? Element) }
                }
            }) {
                axis.ticks.forEach { tick ->
                    SvgTag("line", {
                        attr("x1", padLeft.round()); attr("x2", (width - padRight).round())
                        attr("y1", y(tick).round()); attr("y2", y(tick).round())
                        if (tick == 0.0) {
                            attr("data-chart-zero-baseline", "")
                            attr("stroke", palette.borderStrong.toString())
                            attr("stroke-width", "1.25")
                        } else {
                            attr("stroke", palette.border.toString())
                            attr("stroke-width", "1")
                        }
                    })
                    SvgTag("text", {
                        attr("x", (padLeft - 8).round()); attr("y", (y(tick) + 4).round())
                        attr("text-anchor", "end")
                        attr("fill", palette.textSecondary.toString())
                        attr("font-size", "11")
                    }) { Text(valueFormat(tick)) }
                }

                if (reducedMotion) {
                    SvgTag("g", { attr("data-chart-motion", "static") }) {
                        LineChartMarks(
                            series = series,
                            colors = colors,
                            ring = palette.surface.toString(),
                            steps = steps,
                            area = area,
                            x = { index -> x(index) },
                            y = { value -> y(value) },
                        )
                    }
                } else {
                    // Full-contrast neutral geometry is readable before the animation clock moves;
                    // the sweep reveals series colour and area treatment, not the existence of data.
                    if (previewMounted) SvgTag("g", { attr("data-chart-motion", "preview") }) {
                        LineChartMarks(
                            series = series,
                            colors = List(colors.size) { palette.borderStrong },
                            ring = palette.surface.toString(),
                            steps = steps,
                            area = area,
                            x = { index -> x(index) },
                            y = { value -> y(value) },
                            areaOpacity = LINE_CHART_PREVIEW_AREA_OPACITY,
                        )
                    }
                    SvgTag("g", {
                        attr("data-chart-motion", "reveal")
                        attr("data-chart-reveal-state", if (revealComplete) "complete" else "pending")
                        attr("style", lineChartRevealStyle(revealComplete))
                    }) {
                        LineChartMarks(
                            series = series,
                            colors = colors,
                            ring = palette.surface.toString(),
                            steps = steps,
                            area = area,
                            x = { index -> x(index) },
                            y = { value -> y(value) },
                        )
                    }
                }

                hovered?.let { index ->
                    SvgTag("line", {
                        attr("x1", x(index).round()); attr("x2", x(index).round())
                        attr("y1", padTop.round()); attr("y2", (padTop + plotHeight).round())
                        attr("stroke", palette.borderStrong.toString())
                        attr("stroke-width", "1")
                    })
                    series.forEachIndexed { seriesIndex, one ->
                        one.points.getOrNull(index)?.let { point ->
                            ChartMarker(
                                cx = x(index),
                                cy = y(point.value),
                                shape = ChartPalette.marker(seriesIndex),
                                fill = colors[seriesIndex % colors.size].toString(),
                                ring = palette.surface.toString(),
                            )
                        }
                    }
                }

                listOf(0, steps - 1).distinct().forEach { index ->
                    SvgTag("text", {
                        attr("x", x(index).round())
                        attr("y", (height - 8).toString())
                        attr("text-anchor", if (index == 0) "start" else "end")
                        attr("fill", palette.textSecondary.toString())
                        attr("font-size", "11")
                    }) { Text(series.first().points[index].label) }
                }
            }
        }
        hovered?.let { index ->
            ChartReadout(
                label = series.first().points.getOrNull(index)?.label.orEmpty(),
                entries = series.mapIndexedNotNull { seriesIndex, one ->
                    one.points.getOrNull(index)?.let {
                        Triple(one.name, valueFormat(it.value), colors[seriesIndex % colors.size])
                    }
                },
            )
        }
        ChartTable(
            caption = caption,
            rowHeader = t("ds.ds.lline_chart.step", "Step"),
            columns = series.map { it.name },
            rows = (0 until steps).map { index ->
                series.first().points[index].label to series.map { one ->
                    one.points.getOrNull(index)?.let { valueFormat(it.value) }.orEmpty()
                }
            },
        )
    }
}

internal fun lineChartIndexForClientX(
    clientX: Double,
    elementLeft: Double,
    elementWidth: Double,
    steps: Int,
    logicalWidth: Double,
    plotLeft: Double,
    plotWidth: Double,
): Int {
    if (steps <= 1 || elementWidth <= 0.0 || logicalWidth <= 0.0 || plotWidth <= 0.0) return 0
    val logicalX = ((clientX - elementLeft) / elementWidth) * logicalWidth
    return ((((logicalX - plotLeft) / plotWidth) * (steps - 1)).roundToInt())
        .coerceIn(0, steps - 1)
}

@Composable
private fun rememberLineChartRevealComplete(reducedMotion: Boolean): Boolean {
    var complete by remember { mutableStateOf(reducedMotion) }
    LaunchedEffect(reducedMotion) {
        if (reducedMotion) {
            complete = true
        } else if (!complete) {
            // One frame establishes the initial clip. There is no information delay: the complete
            // preview is already painted underneath, and a suspended frame leaves it readable.
            suspendCancellableCoroutine<Unit> { continuation ->
                window.requestAnimationFrame {
                    if (continuation.isActive) continuation.resume(Unit)
                }
            }
            complete = true
        }
    }
    return reducedMotion || complete
}

private fun lineChartRevealStyle(complete: Boolean): String {
    val clip = if (complete) "inset(0 0 0 0)" else "inset(0 100% 0 0)"
    return "clip-path: $clip; transition: clip-path ${Motion.StandardMs}ms ${Motion.EaseStrongOut};"
}

private const val LINE_CHART_PREVIEW_AREA_OPACITY = 0.0
